import type {
	OnboardingPrefill,
	RealtorClientType,
	RealtorPropertyType,
} from "@shared/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { SingleImageUpload } from "@/components/ui/image-upload";
import { SuccessScreen } from "@/components/ui/success-screen";
import { createRealtorProfile } from "@/features/onboarding/services/onboarding-api.service";
import {
	FieldInput,
	FieldSelect,
	FloatPanel,
	StudioButton,
} from "@/features/studio/theme";
import { useServiceCities } from "@/lib/constants/markets";
import {
	keepOrFill,
	useApplyPrefillOnce,
} from "../hooks/use-onboarding-prefill";
import {
	realtorClientTypeOptions,
	realtorPropertyTypeOptions,
} from "../options";
import { BackLink } from "./back-link";
import { CityChipsPicker, FieldGroupLabel, OptionChips } from "./fields";

const PANEL_PADDING = "clamp(24px, 6vw, 40px) clamp(20px, 5vw, 32px)";

function SectionHeading({ children }: { children: React.ReactNode }) {
	return (
		<h2 className="border-b border-dashboard-border pb-2 font-display text-lg font-semibold text-dashboard-text">
			{children}
		</h2>
	);
}

export interface RealtorOnboardingFlowProps {
	/** Back to the role picker. Without it the form has no Back link. */
	onExit?: () => void;
	/** Rendered in place of the Back link. */
	exitControl?: React.ReactNode;
	/**
	 * Offer the profile photo field. The page sets it while the account adds
	 * this role from another one.
	 */
	showPhoto?: boolean;
	/**
	 * Details the account already gave; fills the name and the photo once
	 * when empty. The form has no email or location field to fill.
	 */
	prefill?: OnboardingPrefill;
	/** The profile was saved; the flow shows its success screen next. */
	onProfileCreated: () => void;
	/** The success screen's button. */
	onFinished: () => void;
}

/** Realtor onboarding: one form, then the success screen. */
export function RealtorOnboardingFlow({
	onExit,
	exitControl,
	showPhoto,
	prefill,
	onProfileCreated,
	onFinished,
}: RealtorOnboardingFlowProps) {
	const { t } = useTranslation();
	const cityOptions = useServiceCities().map((city) => city.value);
	const [isLoading, setIsLoading] = useState(false);
	const [isComplete, setIsComplete] = useState(false);
	const [isUploading, setIsUploading] = useState(false);

	const [photoUrl, setPhotoUrl] = useState("");
	const [name, setName] = useState("");
	const [businessName, setBusinessName] = useState("");
	const [experienceYears, setExperienceYears] = useState("");
	const [primaryCity, setPrimaryCity] = useState("");
	const [additionalCities, setAdditionalCities] = useState<string[]>([]);
	const [propertyTypes, setPropertyTypes] = useState<RealtorPropertyType[]>([]);
	const [clientTypes, setClientTypes] = useState<RealtorClientType[]>([]);

	useApplyPrefillOnce(prefill, (known) => {
		setName((current) => keepOrFill(current, known.name));
		setPhotoUrl((current) => keepOrFill(current, known.profilePictureUrl));
	});

	const choosePrimaryCity = (city: string) => {
		setPrimaryCity(city);
		// A city cannot be both the primary market and an additional one.
		setAdditionalCities((prev) => prev.filter((item) => item !== city));
	};

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();

		if (!name.trim()) {
			toast.error(t("onboarding.realtor.pleaseEnterName"));
			return;
		}
		if (!primaryCity) {
			toast.error(t("onboarding.realtor.choosePrimaryMarket"));
			return;
		}
		if (propertyTypes.length === 0) {
			toast.error(t("onboarding.realtor.selectAtLeastOnePropertyType"));
			return;
		}

		setIsLoading(true);
		try {
			const result = await createRealtorProfile({
				name: name.trim(),
				...(photoUrl ? { profileImageUrl: photoUrl } : {}),
				agencyName: businessName.trim() || undefined,
				yearsExperience: experienceYears
					? Number.parseInt(experienceYears, 10)
					: undefined,
				primaryCity,
				serviceCities: [
					primaryCity,
					...additionalCities.filter((city) => city !== primaryCity),
				],
				propertyTypes,
				clientTypes,
			});

			if (result.success) {
				onProfileCreated();
				toast.success(t("onboarding.realtor.profileCreated"));
				setIsComplete(true);
			} else {
				toast.error(
					result.error || t("onboarding.realtor.failedToCreateProfile"),
				);
			}
		} catch {
			toast.error(t("common.somethingWentWrong"));
		} finally {
			setIsLoading(false);
		}
	};

	if (isComplete) {
		return (
			<SuccessScreen
				title={t("onboarding.realtor.completeTitle")}
				description={t("onboarding.realtor.completeSubtitle")}
				actionLabel={t("common.goToDashboard")}
				onAction={onFinished}
				notice={{
					title: t("onboarding.realtor.verificationPendingTitle"),
					message: t("onboarding.realtor.verificationPendingMsg"),
				}}
			/>
		);
	}

	return (
		<>
			{exitControl}
			{onExit && <BackLink onClick={onExit} />}

			<div className="mb-8 text-center">
				<h1 className="mb-2 font-display text-[28px] font-semibold text-dashboard-text">
					{t("onboarding.realtor.title")}
				</h1>
				<p className="font-body text-sm text-dashboard-muted">
					{t("onboarding.realtor.subtitle")}
				</p>
			</div>

			<FloatPanel elevation={2} padding={PANEL_PADDING}>
				<form onSubmit={handleSubmit} className="space-y-8">
					<section className="space-y-5">
						<SectionHeading>
							{t("onboarding.realtor.businessSection")}
						</SectionHeading>
						{showPhoto && (
							<div>
								<FieldGroupLabel>
									{t("onboarding.fields.profilePhoto")}
								</FieldGroupLabel>
								<SingleImageUpload
									label=""
									currentUrl={photoUrl || undefined}
									onUpload={setPhotoUrl}
									onRemove={() => setPhotoUrl("")}
									onUploadingChange={setIsUploading}
								/>
							</div>
						)}
						<FieldInput
							id="realtor-name"
							label={t("onboarding.realtor.name")}
							type="text"
							value={name}
							autoComplete="name"
							onChange={(event) => setName(event.target.value)}
							placeholder={t("onboarding.realtor.namePlaceholder")}
							required
						/>
						<FieldInput
							id="realtor-business-name"
							label={t("onboarding.realtor.businessName")}
							type="text"
							value={businessName}
							autoComplete="organization"
							maxLength={200}
							onChange={(event) => setBusinessName(event.target.value)}
							placeholder={t("onboarding.realtor.businessNamePlaceholder")}
						/>
						<FieldSelect
							id="realtor-experience"
							label={t("onboarding.realtor.yearsOfExperience")}
							value={experienceYears || "__none__"}
							onChange={(event) => {
								const value = event.target.value;
								setExperienceYears(value === "__none__" ? "" : value);
							}}
							options={[
								{
									value: "__none__",
									label: t("onboarding.realtor.selectExperience"),
								},
								{ value: "1", label: t("onboarding.realtor.exp1to2") },
								{ value: "3", label: t("onboarding.realtor.exp3to5") },
								{ value: "6", label: t("onboarding.realtor.exp6to10") },
								{ value: "11", label: t("onboarding.realtor.exp10to15") },
								{ value: "16", label: t("onboarding.realtor.exp15plus") },
							]}
						/>
					</section>

					<section className="space-y-5">
						<SectionHeading>
							{t("onboarding.realtor.marketsSection")}
						</SectionHeading>
						<FieldSelect
							id="realtor-primary-market"
							label={t("onboarding.realtor.primaryMarket")}
							value={primaryCity || "__none__"}
							onChange={(event) =>
								choosePrimaryCity(
									event.target.value === "__none__" ? "" : event.target.value,
								)
							}
							options={[
								{
									value: "__none__",
									label: t("onboarding.realtor.selectPrimaryMarket"),
								},
								...cityOptions.map((city) => ({ value: city, label: city })),
							]}
						/>
						<CityChipsPicker
							label={t("onboarding.realtor.additionalMarkets")}
							hint={t("onboarding.realtor.additionalMarketsHint")}
							cities={cityOptions}
							selected={additionalCities}
							onChange={setAdditionalCities}
							exclude={primaryCity || undefined}
							addPlaceholder={t("onboarding.designer.addCityPlaceholder")}
						/>
					</section>

					<section className="space-y-6">
						<SectionHeading>
							{t("onboarding.realtor.specializationSection")}
						</SectionHeading>
						<OptionChips
							label={t("onboarding.realtor.propertyTypes")}
							options={realtorPropertyTypeOptions(t)}
							selected={propertyTypes}
							onChange={(next) =>
								setPropertyTypes(next as RealtorPropertyType[])
							}
						/>
						<OptionChips
							label={t("onboarding.realtor.clientTypes")}
							options={realtorClientTypeOptions(t)}
							selected={clientTypes}
							onChange={(next) => setClientTypes(next as RealtorClientType[])}
						/>
					</section>

					<StudioButton
						type="submit"
						variant="primary"
						disabled={isLoading || isUploading}
						style={{ width: "100%" }}
					>
						{isLoading
							? t("common.creating")
							: t("onboarding.realtor.createProfile")}
					</StudioButton>
				</form>
			</FloatPanel>
		</>
	);
}
